import { z } from "zod";
import { TimestampField } from "./common";

/**
 * Hierarchical Role System
 *
 * Roles are ordered by privilege level (index in array).
 * Higher privilege roles inherit all permissions from lower privilege roles.
 *
 * Hierarchy: guest < registered < admin < system
 *
 * - guest: Unauthenticated or minimal access
 * - registered: Authenticated user with basic permissions
 * - admin: Full workspace administration capabilities
 * - system: Application/service accounts with highest privileges
 */

// Define role hierarchy as const array for type safety and runtime access
export const ROLE_HIERARCHY = [
  "guest",
  "registered",
  "assistant",
  "admin",
  "system",
] as const;

// Derive Role type from hierarchy
export type Role = (typeof ROLE_HIERARCHY)[number];

// Role schema with validation
export const RoleSchema = z.enum(ROLE_HIERARCHY);

/**
 * Get privilege level of a role
 * Higher number = higher privilege
 */
export function getRoleLevel(role: Role): number {
  return ROLE_HIERARCHY.indexOf(role);
}

/**
 * Check if a user's role satisfies the required role
 * Uses hierarchical comparison: higher roles inherit lower role permissions
 *
 * @param userRole - The role assigned to the user
 * @param requiredRole - The minimum role required for access
 * @returns true if userRole has equal or higher privilege than requiredRole
 *
 * @example
 * hasRoleAccess("admin", "registered") // true - admin inherits registered
 * hasRoleAccess("registered", "admin") // false - registered lacks admin privilege
 * hasRoleAccess("registered", "registered") // true - exact match
 */
export function hasRoleAccess(userRole: Role, requiredRole: Role): boolean {
  const userLevel = getRoleLevel(userRole);
  const requiredLevel = getRoleLevel(requiredRole);
  return userLevel >= requiredLevel;
}

/**
 * Get all roles that a user with given role can access
 * Returns the user's role and all lower privilege roles
 *
 * @example
 * getInheritedRoles("admin") // ["guest", "registered", "admin"]
 * getInheritedRoles("registered") // ["guest", "registered"]
 */
export function getInheritedRoles(role: Role): Role[] {
  const level = getRoleLevel(role);
  return ROLE_HIERARCHY.slice(0, level + 1) as Role[];
}

/**
 * Session with role information
 */
export const SessionWithRole = z.object({
  userId: z.string(),
  username: z.string(),
  email: z.email(),
  displayName: z.string(),
  avatar: z.string(),
  workspaceId: z.string(),
  participantId: z.string(),
  role: RoleSchema,
  expires: z.number(),
  createdAt: TimestampField,
});

/**
 * Role assignment/update request
 */
export const RoleUpdateRequest = z.object({
  userId: z.string(),
  role: RoleSchema,
  workspaceId: z.string().optional(),
});

/**
 * Role check result
 */
export const RoleCheckResult = z.object({
  hasAccess: z.boolean(),
  userRole: RoleSchema,
  requiredRole: RoleSchema,
  reason: z.string().optional(),
});

/**
 * Authorization error details
 */
export const AuthorizationError = z.object({
  error: z.string(),
  requiredRole: RoleSchema,
  userRole: RoleSchema.optional(),
  message: z.string(),
});

// Export all schemas
export const schemas = {
  RoleSchema,
  SessionWithRole,
  RoleUpdateRequest,
  RoleCheckResult,
  AuthorizationError,
};

// oRPC Type Inference Support
export type SchemaTypes = {
  [K in keyof typeof schemas]: z.infer<(typeof schemas)[K]>;
};

// Re-export for convenient access
export type SessionWithRoleType = z.infer<typeof SessionWithRole>;
export type RoleUpdateRequestType = z.infer<typeof RoleUpdateRequest>;
export type RoleCheckResultType = z.infer<typeof RoleCheckResult>;
export type AuthorizationErrorType = z.infer<typeof AuthorizationError>;
